// GUS-60 — Reference data for the prescription editor & printout.
// Each entry stores a stable `code` (persisted to the DB) plus Hebrew/English labels
// so the value prints language-independently. Sourced from the clinical tables
// supplied with the feature request.

export interface LookupItem {
  code: string;
  he: string;
  en: string;
}

// Units of measure (יחידות מידה)
export const UNITS: LookupItem[] = [
  { code: 'mg',      he: 'מ"ג',        en: 'mg' },
  { code: 'mcg',     he: 'מק"ג',       en: 'mcg' },
  { code: 'g',       he: 'גרם',        en: 'g' },
  { code: 'mL',      he: 'מ"ל',        en: 'mL' },
  { code: 'L',       he: 'ליטר',       en: 'L' },
  { code: 'mg/mL',   he: 'מ"ג/מ"ל',    en: 'mg/mL' },
  { code: 'mg/5mL',  he: 'מ"ג/5 מ"ל',  en: 'mg/5 mL' },
  { code: '%',       he: 'אחוז',       en: '%' },
  { code: 'IU',      he: 'יח\' בינ"ל', en: 'IU' },
  { code: 'U',       he: 'יחידות',     en: 'U' },
  { code: 'Tablet',  he: 'טבליה',      en: 'Tablet' },
  { code: 'Capsule', he: 'כמוסה',      en: 'Capsule' },
  { code: 'Sachet',  he: 'שקית',       en: 'Sachet' },
  { code: 'Drops',   he: 'טיפות',      en: 'Drops' },
  { code: 'Spray',   he: 'תרסיס',      en: 'Spray' },
  { code: 'Puff',    he: 'שאיפה',      en: 'Puff / Inhalation' },
  { code: 'Patch',   he: 'מדבקה',      en: 'Patch' },
  { code: 'Suppository', he: 'נר',     en: 'Suppository' },
  { code: 'Enema',   he: 'חוקן',       en: 'Enema' },
  { code: 'Ampoule', he: 'אמפולה',     en: 'Ampoule' },
  { code: 'Vial',    he: 'בקבוקון',    en: 'Vial' },
  { code: 'Bottle',  he: 'בקבוק',      en: 'Bottle' },
  { code: 'Tube',    he: 'שפופרת',     en: 'Tube' },
];

// Administration instruction (אופן מתן) - relation to food/sleep, dosing pattern
export const TIMINGS: LookupItem[] = [
  { code: 'HS',        he: 'לפני השינה',        en: 'At bedtime (HS / qHS)' },
  { code: 'AC',        he: 'לפני אוכל',         en: 'Before meals (AC)' },
  { code: 'PC',        he: 'אחרי אוכל',         en: 'After meals (PC)' },
  { code: 'WithFood',  he: 'עם אוכל',           en: 'With food' },
  { code: 'WithoutFood', he: 'בלי אוכל / על קיבה ריקה', en: 'Without food / On an empty stomach' },
  { code: 'AnyFood',   he: 'ללא קשר לאוכל',     en: 'With or without food' },
  { code: 'PRN',       he: 'לפי הצורך',         en: 'As needed (PRN)' },
  { code: 'Stat',      he: 'מנה חד-פעמית',      en: 'Single dose / Stat dose' },
  { code: 'Loading',   he: 'מנת העמסה',         en: 'Loading dose' },
  { code: 'Maintenance', he: 'טיפול אחזקה',     en: 'Maintenance dose' },
  { code: 'MorningAC', he: 'בבוקר לפני אוכל',   en: 'In the morning before food' },
  { code: 'EveningPC', he: 'בערב אחרי אוכל',    en: 'In the evening after food' },
  { code: 'EveningAC', he: 'בערב לפני אוכל',    en: 'In the evening before food' },
];

// Route of administration (דרך מתן)
export const ROUTES: LookupItem[] = [
  { code: 'PO',           he: 'פומי / דרך הפה',        en: 'Oral (PO)' },
  { code: 'SL',           he: 'תת-לשוני',              en: 'Sublingual (SL)' },
  { code: 'Buccal',       he: 'בוקאלי',                en: 'Buccal' },
  { code: 'PR',           he: 'רקטלי',                 en: 'Rectal (PR)' },
  { code: 'Vaginal',      he: 'נרתיקי',                en: 'Vaginal' },
  { code: 'IV',           he: 'תוך-ורידי',             en: 'Intravenous (IV)' },
  { code: 'IM',           he: 'תוך-שרירי',             en: 'Intramuscular (IM)' },
  { code: 'SC',           he: 'תת-עורי',               en: 'Subcutaneous (SC/SQ)' },
  { code: 'ID',           he: 'תוך-עורי',              en: 'Intradermal (ID)' },
  { code: 'Inhalation',   he: 'שאיפה / אינהלציה',      en: 'Inhalation' },
  { code: 'Nasal',        he: 'אפי / נזאלי',           en: 'Nasal / Intranasal' },
  { code: 'Ophthalmic',   he: 'עיני',                  en: 'Ophthalmic / Ocular' },
  { code: 'Otic',         he: 'אוזני',                 en: 'Otic / Auricular' },
  { code: 'Topical',      he: 'עורי / מקומי',          en: 'Topical / Cutaneous' },
  { code: 'Transdermal',  he: 'טרנסדרמלי',             en: 'Transdermal' },
  { code: 'Intrathecal',  he: 'תוך-שדרתי / אינטרה-תקלי', en: 'Intrathecal' },
  { code: 'Epidural',     he: 'אפידורלי',              en: 'Epidural' },
  { code: 'IntraArticular', he: 'תוך-מפרקי',           en: 'Intra-articular' },
  { code: 'IO',           he: 'תוך-עצמי',              en: 'Intraosseous (IO)' },
  { code: 'IA',           he: 'תוך-עורקי',             en: 'Intra-arterial (IA)' },
  { code: 'IP',           he: 'תוך-צפקי',              en: 'Intraperitoneal (IP)' },
  { code: 'Intravesical', he: 'תוך-שלפוחיתי',          en: 'Intravesical' },
];

// Frequency (תדירות) - how often the dose is taken
export const FREQUENCIES: LookupItem[] = [
  { code: 'QD',        he: 'פעם ביום',            en: 'Once daily (QD)' },
  { code: 'BID',       he: 'פעמיים ביום',          en: 'Twice daily (BID)' },
  { code: 'TID',       he: 'שלוש פעמים ביום',      en: 'Three times daily (TID)' },
  { code: 'QID',       he: 'ארבע פעמים ביום',      en: 'Four times daily (QID)' },
  { code: 'Q4H',       he: 'כל 4 שעות',            en: 'Every 4 hours' },
  { code: 'Q6H',       he: 'כל 6 שעות',            en: 'Every 6 hours' },
  { code: 'Q8H',       he: 'כל 8 שעות',            en: 'Every 8 hours' },
  { code: 'Q12H',      he: 'כל 12 שעות',           en: 'Every 12 hours' },
  { code: 'Q24H',      he: 'כל 24 שעות',           en: 'Every 24 hours' },
  { code: 'QOD',       he: 'יום כן יום לא',         en: 'Every other day (QOD)' },
  { code: 'Weekly',    he: 'פעם בשבוע',            en: 'Once weekly' },
  { code: 'Weekly2',   he: 'פעמיים בשבוע',         en: 'Twice weekly' },
  { code: 'Weekly3',   he: 'שלוש פעמים בשבוע',     en: 'Three times weekly' },
  { code: 'Biweekly',  he: 'כל שבועיים',           en: 'Every 2 weeks' },
  { code: 'Monthly',   he: 'פעם בחודש',            en: 'Once monthly' },
  { code: 'Quarterly', he: 'כל 3 חודשים',          en: 'Every 3 months' },
  { code: 'EveryAM',   he: 'כל בוקר',              en: 'Every morning' },
  { code: 'EveryPM',   he: 'כל ערב',               en: 'Every evening' },
];
// Sentinel code for "type it yourself" - not looked up, triggers a free-text input
export const FREQUENCY_OTHER = 'OTHER';

// Interval time-units for the "משך" field (supports e.g. once every 3 months)
export const INTERVAL_UNITS: LookupItem[] = [
  { code: 'day',   he: 'ימים',   en: 'days' },
  { code: 'week',  he: 'שבועות', en: 'weeks' },
  { code: 'month', he: 'חודשים', en: 'months' },
];

function labelFrom(list: LookupItem[], code: string, lang: string): string {
  if (!code) { return ''; }
  const item = list.find(x => x.code === code);
  if (!item) { return code; }
  return lang === 'en' ? item.en : item.he;
}

export function unitLabel(code: string, lang: string): string { return labelFrom(UNITS, code, lang); }
export function timingLabel(code: string, lang: string): string { return labelFrom(TIMINGS, code, lang); }
export function routeLabel(code: string, lang: string): string { return labelFrom(ROUTES, code, lang); }
export function intervalUnitLabel(code: string, lang: string): string { return labelFrom(INTERVAL_UNITS, code, lang); }
export function frequencyLabel(code: string, lang: string): string { return labelFrom(FREQUENCIES, code, lang); }
